import PropTypes from 'prop-types';
import {
    addDate,
    diffInDay,
    findPromoteMonth,
    formatDate,
    getEndOfMonth,
    getStartOfMonth,
    inRange,
    parseDate,
} from '@jd/jmtd-tools/dist/date';
import React, {PureComponent} from 'react';
import DatePicker from '@jd/jmtd/es/DatePicker';
import {fixZero, formatDay} from '@jd/jmtd/es/tools/date';
import {findYOY, Lunar, toString} from '@jd/jmtd-tools/es/lunar';
import QuickPanel, {getValueFromQuickOption} from '@jd/jmtd/es/QuickPanel';
import DayPanel from '@jd/jmtd/es/DayPanel';
import RangeTimePanel from '@jd/jmtd/es/RangeTimePanel';
import Form from '@jd/jmtd/es/Form';
import FormItem from '@jd/jmtd/es/FormItem';
import ButtonGroup from '@jd/jmtd/es/ButtonGroup';
import Button from '@jd/jmtd/es/Button';
import Icon from '@jd/jmtd/es/Icon';
import Annotation from '@jd/jmtd/es/Annotation';
import Placeholder from '@jd/jmtd/es/Placeholder';
import {clone} from '@jd/jmtd-tools/dist/object';
import RangePanel from '@jd/jmtd/es/RangePanel';
import DateTimePanel from '@jd/jmtd/es/DateTimePanel';
import {bubble} from '@jd/jmtd-tools/dist/element';
import {findFirst} from '@jd/jmtd-tools/dist/generator';
import {createPortal} from 'react-dom';
import DayTimePanel from '@jd/jmtd/es/DayTimePanel';
import * as localStorage from '@jd/jmtd-tools/dist/local-storage';
import ModalTips from '@jd/jmtd/es/ModalTips';
import ModalFooter from '@jd/jmtd/es/ModalFooter';
import Badge from '@jd/jmtd/es/Badge';
import Tag from '@jd/jmtd/es/Tag';
import {addParams} from '@jd/jmtd-tools/dist/url';
import {ajax} from '@jd/jmtd-pro/es/tools/ajax';
import {FormGroup} from '@jd/jmtd-pro';
import {getDucc, isOnline} from '@jd/jmtd-pro/es/geEnv';
import RuleDescription from './RuleDescription';
import {formatTimeGroupConfig} from './func'

// 动作
const $$refresh = Symbol('刷新');
const $$updateRealtime = Symbol('更新实时');
const $$updateCompareTime = Symbol('更新对比');
const $$countdown = Symbol('倒计时');
const $$refreshDay = Symbol('跨零点刷新');
const $$refreshDayUpdate = Symbol('跨零点刷新生效');
const $$operation = Symbol('快捷选项');
const $$userAction = Symbol('用户动作');
const $$updateDebugTime = Symbol('更新调试时间');

const defaultRefreshDayRandom = () => (Math.random() * 20000) << 0; // 零点刷新随机打散
const defaultRefreshInterval = 10000;

let panelKey = 0;

function timeToSeconds(v, end) {
    const [a = 0, b = 0, c = 0] = v.split(':').map(x => parseInt(x, 10));
    return a * 3600 + (end ? b || 59 : b) * 60 + c;
}

function parseTime(d, t) {
    const [Y, M, D] = d.split('-').map(x => parseInt(x, 10));
    const [h, m, s] = t.split(':').map(x => parseInt(x, 10));
    return new Date(Y, M - 1, D, h, m, s);
}

// 判断是否在时间范围内
function inTimeRange([start, end], date) {
    const parseT = x => parseTime(...x.split(' '));
    return inRange(date, parseT(start), parseT(end));
}

// 快捷选项回显
function QuickOptionEcho({value}) {
    const item = value?.item;
    if (!item?.label) {
        return null;
    }

    return (
        <Tag className='jmtp-ge-realtime-compare-time-group-new-quick-option-echo'>
            {item.label}
        </Tag>
    );
}

QuickOptionEcho.propTypes = {
    value: PropTypes.object,
};

QuickOptionEcho.defaultProps = {
    value: null,
};

// 定制的范围日期时间面板
const SpecialRangeDayTimePanel = {
    type: 'rangeDayTime',
    echo: ([[ds, ts], [de, te]]) => (
        <>
            {ds ?? <Placeholder>起始日期</Placeholder>} {ts ?? <Placeholder>起始时间</Placeholder>}
            <br/>
            {de ?? <Placeholder>结束日期</Placeholder>} {te ?? <Placeholder>结束时间</Placeholder>}
        </>
    ),
    render({
               value,
               state,
               update,
               config: {startTimeConfig, endTimeConfig, dateConfig, isItemDisabled},
           }) {
        value = clone(value ?? [[], []]);

        const {dropdown: RangePanelDropdown} = RangePanel.render({
            value: [value[0][0], value[1][0]],
            state,
            update: newValue => {
                if (newValue) {
                    value[0][0] = newValue[0];
                    value[1][0] = newValue[1];
                    value[0][1] = null;
                    value[1][1] = null;
                    update(value);
                } else {
                    update();
                }
            },
            config: {
                ...dateConfig,
                updateImmediately: true,
            },
        });

        const {dropdown: customTimePanelDropdown} = RangeTimePanel.render({
            value: [value[0][1], value[1][1]],
            state,
            update: newTime => {
                if (newTime) {
                    value[0][1] = newTime[0];
                    value[1][1] = newTime[1];
                    update(value);
                } else {
                    update();
                }
            },
            config: {
                isItemDisabled: (ts, te) => isItemDisabled(ts, te, value),
                startConfig: {
                    ...startTimeConfig,
                    isValueDisabled: v => startTimeConfig.isValueDisabled(v, value),
                },
                endConfig: {
                    ...endTimeConfig,
                    isValueDisabled: v => endTimeConfig.isValueDisabled(v, value),
                },
            },
        });

        return {
            defaultValue: [[], []], // 历史原因兼容模式，用于disabledClose
            notCloseWhenUpdate: true,
            dropdown: (
                <DateTimePanel
                    datePanel={RangePanelDropdown}
                    timePanel={customTimePanelDropdown}
                    state={state}
                    update={update}
                />
            ),
        };
    },
};

// 判断页面是否可见
const isDocumentVisible = () => !document.visibilityState || document.visibilityState === 'visible';

const refreshTiming = d => d.getDate();

class Time extends PureComponent {
    componentDidMount() {
        // 每秒刷新
        const refresh = () => {
            setTimeout(() => {
                if (!this.unmounted) {
                    this.forceUpdate();
                    refresh();
                }
            }, 1000);
        };
        refresh();
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    render() {
        const {getNow} = this.props;

        return formatDate(getNow(), 'YYYY-MM-DD hh:mm:ss');
    }
}

Time.propTypes = {
    getNow: PropTypes.func.isRequired,
};

// 时间——对比时间组件
class ComboTimePicker extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            showModal: false,
            focusedOperation: null,
            currentDescriptionTab: 'rule',
            descriptionOpen: false,
        };
    }

    componentDidMount() {
        const {
            props: {getNow, dispatch, getServerNow, disableAutoRefresh},
        } = this;
        let lastDay = getNow();
        let lastHour = getNow();

        const check = async () => {
            if (this.unmounted) {
                return;
            }
            let serverNow;

            const {operations} = this.props;
            const now = getNow();

            // 弹框判断
            checkPrompt: {
                for (const operation of operations) {
                    for (const {isShown, key, message} of operation.promptList ?? []) {
                        const showed = localStorage.getItem(key);
                        if (!showed && isShown(now)) {
                            serverNow ??= await getServerNow();
                            if (isShown(serverNow)) {
                                localStorage.setItem(key, 1);
                                this.showPrompt = true;
                                this.setState({
                                    prompt: {
                                        operation,
                                        message,
                                    },
                                });
                            }
                            break checkPrompt;
                        }
                    }
                }
            }

            // 零点刷新判断
            if (refreshTiming(now) !== refreshTiming(lastDay)) {
                // 如果服务器时间和本地时间是同一天，更新last，触发回调
                serverNow ??= await getServerNow();
                if (refreshTiming(serverNow) === refreshTiming(now)) {
                    // 刷新条件：页面tab可见，未禁用自动刷新，未显示弹窗
                    if (isDocumentVisible() && !disableAutoRefresh && !this.showPrompt) {
                        dispatch($$refreshDay);
                    }
                    lastDay = now;
                }
            } else if (now.getHours() !== lastHour.getHours()) {
                this.forceUpdate();
                lastHour = now;
            }

            setTimeout(check, 1000);
        };

        check();
        this.componentDidUpdate();
    }

    componentDidUpdate() {
        // 如果包裹在card中，为card-header添加一个独特的class控制，用于扩展高度
        const cardHeader = findFirst(bubble(this.placeholder), el =>
            el.classList ? el.classList.contains('jmtd-card-header') : false,
        );
        if (cardHeader) {
            cardHeader.classList.add('jmtp-ge-realtime-compare-time-group-header');
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    render() {
        const {
            props: {
                valueKey,
                value,
                disabledRefresh, // 禁用刷新和时间组件
                countdown,
                dispatch,
                panels,
                comparePanels,
                operations: rawOperations,
                debugMode = false,
                debugTime,
                getNow,
                disableAll, // 禁用刷新和快捷选项
            },
            state: {currentDescriptionTab, descriptionOpen, focusedOperation, prompt},
        } = this;

        // 计算要渲染的选项
        const operations = disableAll
            ? []
            : rawOperations
                .filter(({isShown}) => !isShown || isShown())
                .map(({isDisabled = () => false, disabled, ...option}) => ({
                    ...option,
                    disabled: disabled || isDisabled(),
                }));

        const descriptionOperations = operations.filter(o => o.description);

        const buttonProps = {
            className: 'jmtp-ge-realtime-picker-refresh-button',
            outline: true,
        };

        return (
            <>
                <Form inline>
                    <FormItem label='时间'>
                        <ButtonGroup>
                            <DatePicker
                                valueKey={valueKey}
                                className='jmtp-ge-realtime-compare-time-group-new-realtime'
                                icon={
                                    <>
                                        <QuickOptionEcho value={value.realtime}/>
                                        <Icon
                                            className='jmti-backward jmtp-ge-realtime-compare-time-group-compare-echo-arrow'/>
                                    </>
                                }
                                noSelectorLabel
                                disabled={disabledRefresh}
                                placement='right'
                                panels={panels}
                                value={value.realtime}
                                showConfirm
                                onChange={v => {
                                    dispatch($$updateRealtime, v);
                                }}
                                dropdownButtonPanelLeft={({setOpen}) =>
                                    operations.map((item, index) => {
                                        if (item.divider) {
                                            if (index === operations.length - 1) {
                                                return null;
                                            }

                                            return (
                                                <div
                                                    key={index}
                                                    className='jmtp-ge-realtime-compare-time-group-new-operation-divider'
                                                ></div>
                                            );
                                        }
                                        let recommend = false;
                                        if (item.recommend) {
                                            for (const match of item.recommend) {
                                                if (match()) {
                                                    recommend = true;
                                                    break;
                                                }
                                            }
                                        }

                                        return (
                                            <Button
                                                key={index}
                                                className='jmtd-date-picker-fast'
                                                data-event-name='operation'
                                                data-event-content={null}
                                                data-event-content-selector='.jmtp-ge-realtime-compare-time-group-new-operation-label'
                                                size='mini'
                                                semantic='primary'
                                                link
                                                disabled={item.disabled}
                                                onClick={() => {
                                                    dispatch($$operation, item);
                                                    setOpen(false);
                                                }}
                                            >
                                                <span
                                                    className='jmtp-ge-realtime-compare-time-group-new-operation-label'>
                                                    {item.label}
                                                </span>
                                                {item.description && (
                                                    <Icon
                                                        data-event-name='info'
                                                        className='jmti-info-circle'
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            this.setState({
                                                                descriptionOpen: true,
                                                                focusedOperation: item,
                                                            });
                                                            setOpen(false);
                                                        }}
                                                    />
                                                )}
                                                {recommend && (
                                                    <Badge
                                                        className='jmtp-ge-realtime-compare-time-group-new-operation-recommend'
                                                        count='荐'
                                                    />
                                                )}
                                            </Button>
                                        );
                                    })
                                }
                                switchPanelChildren={({setOpen}) =>
                                    !!descriptionOperations?.length && (
                                        <Tag
                                            data-component-name='Button'
                                            data-event-name='click'
                                            data-event-content
                                            className='jmtp-ge-realtime-compare-time-group-new-realtime-rule-tag'
                                            size='small'
                                            color='amber'
                                            bordered
                                            endIcon={<Icon className='jmti-info-circle'/>}
                                            onClick={() => {
                                                this.setState({
                                                    descriptionOpen: true,
                                                });
                                                setOpen(false);
                                            }}
                                        >
                                            规则说明
                                        </Tag>
                                    )
                                }
                            />
                            {disabledRefresh || disableAll ? (
                                countdown === null ? (
                                    <Button
                                        {...buttonProps}
                                        disabled
                                        startIcon={<Icon className='jmti-refresh'/>}
                                    />
                                ) : (
                                    <Button {...buttonProps} disabled>
                                        {countdown}s
                                    </Button>
                                )
                            ) : (
                                <Button
                                    data-event-name='refresh'
                                    {...buttonProps}
                                    startIcon={<Icon className='jmti-refresh'/>}
                                    onClick={() => {
                                        dispatch($$refresh);
                                    }}
                                />
                            )}
                        </ButtonGroup>
                    </FormItem>
                    <FormItem label='对比时间'>
                        <div clsTag={'h|keycount|yx_center_hzcp_1678258944439|9'}>
                            <DatePicker
                              valueKey={valueKey}
                              icon={
                                  <Icon className='jmti-backward jmtp-ge-realtime-compare-time-group-compare-echo-arrow'/>
                              }
                              noSelectorLabel
                              disabled={disabledRefresh}
                              placement='right'
                              panels={comparePanels}
                              value={value.compareTime}
                              showConfirm
                              onChange={v => {
                                  dispatch($$updateCompareTime, v);
                              }}
                            />
                        </div>
                    </FormItem>
                </Form>
                {/* 占位元素，用于获取父元素 */}
                <div
                    className='jmtp-ge-realtime-compare-time-group-placeholder'
                    ref={el => {
                        this.placeholder = el;
                    }}
                ></div>
                <RuleDescription
                    operations={descriptionOperations}
                    currentTab={currentDescriptionTab}
                    focusedOperation={focusedOperation}
                    open={descriptionOpen}
                    onOperationChange={v => {
                        this.setState({
                            focusedOperation: v,
                        });
                    }}
                    onTabChange={v => {
                        this.setState({
                            currentDescriptionTab: v,
                        });
                    }}
                    getNow={getNow}
                    onClose={() => {
                        this.setState({
                            descriptionOpen: false,
                        });
                    }}
                />
                {/* 推送弹框 */}
                <ModalTips
                    open={!!prompt}
                    type='question'
                    explainText={prompt?.message}
                    operateBtn={
                        <ModalFooter pos='center' noBorder>
                            <Button
                                size='small'
                                onClick={() => {
                                    this.setState({
                                        prompt: null,
                                    });
                                    this.showPrompt = false;
                                }}
                            >
                                取消
                            </Button>
                            <Button
                                semantic='primary'
                                size='small'
                                onClick={() => {
                                    this.setState({
                                        prompt: null,
                                    });
                                    this.showPrompt = false;
                                    dispatch($$operation, prompt.operation);
                                }}
                            >
                                确定
                            </Button>
                        </ModalFooter>
                    }
                />
                {/* 调试器 */}
                {debugMode &&
                createPortal(
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'fixed',
                            zIndex: 10000,
                            left: 0,
                            top: 0,
                            background: 'yellow',
                        }}
                    >
                        调试器
                        <DatePicker
                            placeholder='设置时间，默认使用系统时间'
                            value={{
                                type: 'dayTime',
                                value: [
                                    formatDay(debugTime),
                                    formatDate(debugTime, 'hh:mm:ss'),
                                ],
                            }}
                            panels={[
                                {
                                    ...DayTimePanel,
                                    timeConfig: {
                                        showHour: true,
                                        showMinute: true,
                                        showSecond: true,
                                    },
                                    isDisableClose([v1, v2]) {
                                        return !v1 || !v2;
                                    },
                                },
                            ]}
                            onChange={({value: [d, t]}) => {
                                dispatch($$updateDebugTime, parseTime(d, t));
                            }}
                            showConfirm
                            showClear
                        />
                        <Time getNow={getNow}/>
                        <Button
                            title='用于重复测试弹框'
                            onClick={() => {
                                for (const operation of operations) {
                                    for (const {key} of operation.promptList ?? []) {
                                        localStorage.removeItem(key);
                                    }
                                }
                            }}
                        >
                            清除弹框缓存
                        </Button>
                    </div>,
                    document.body,
                )}
            </>
        );
    }
}

ComboTimePicker.propTypes = {
    comparePanels: PropTypes.array.isRequired,
    disabledRefresh: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    panels: PropTypes.array.isRequired,
    value: PropTypes.object.isRequired,
    countdown: PropTypes.number,
    getNow: PropTypes.func.isRequired,
    getServerNow: PropTypes.func.isRequired,
    operations: PropTypes.array,
    valueKey: PropTypes.number.isRequired,
    debugMode: PropTypes.bool,
    debugTime: PropTypes.any,
    disableAll: PropTypes.bool,
    disableAutoRefresh: PropTypes.bool,
};
ComboTimePicker.defaultProps = {
    countdown: null,
    disabledRefresh: false,
    operations: [],
    debugMode: false,
    debugTime: null,
    disableAll: false,
    disableAutoRefresh: false,
};

const valueDestructuring = ({realtime, compareTime}) => {
    // console.log({realtime, compareTime})
    let params = {};
    // console.log(compareTime?.value.key)
    const customCompareType =  compareTime?.value?.key
    if (compareTime.type === 'quick') {
        compareTime = compareTime.value;
    }

    const isCustomCmp = compareTime.type === 'rangeDayTime';
    const compareDateStr = isCustomCmp ? compareTime.value[1][0] : compareTime.value;
    const realtimeKey = realtime.key;
    const quickCompareType = realtime.quickCompareType;
    let [customDs, customTs] = ['', '']; // 自定义对比 - 起始日期&时间
    let [customDe, customTe] = ['', '']; // 自定义对比 - 结束日期&时间
    if (isCustomCmp) {
        [customDs, customTs] = compareTime.value[0];
        [customDe, customTe] = compareTime.value[1];
    }


    if (realtimeKey === 'today' || realtimeKey === 'yesterday') {
        // 单天
        const [startIndex, endIndex] = realtime.value;
        params = {
            realTime: realtime.day,
            compareTime: compareDateStr,
            date: compareDateStr,
            startIndex,
            endIndex,
            customCompareType: customCompareType
        };
        const cmpParams = {
            customRange: isCustomCmp && customDs !== customDe, // 自定义对比且跨天
            rangeStartTime: `${realtime.day} ${startIndex}`,
            rangeEndTime: `${realtime.day} ${endIndex}`,
            rangeCompareStartTime: isCustomCmp
                ? `${customDs} ${customTs}`
                : `${compareDateStr} ${startIndex}`,
            rangeCompareEndTime: isCustomCmp
                ? `${customDe} ${customTe}`
                : `${compareDateStr} ${endIndex}`,
            compareStartIndex: isCustomCmp
                ? customDs === customDe
                    ? customTs
                    : '00:00:00'
                : startIndex,
            compareEndIndex: isCustomCmp ? customTe : endIndex,
        };
        params = {
            ...params,
            ...cmpParams,
        };
    } else {
        // 范围
        const [[ds, ts], [de, te]] = realtime.value;
        if (ds === de) {
            params = {
                realTime: ds,
                compareTime: compareDateStr,
                date: compareDateStr,
                startIndex: ts,
                endIndex: te,
                customCompareType: customCompareType
            };
            const cmpParams = {
                customRange: isCustomCmp && customDs !== customDe,
                rangeStartTime: `${ds} ${ts}`,
                rangeEndTime: `${de} ${te}`,
                rangeCompareStartTime: isCustomCmp
                    ? `${customDs} ${customTs}`
                    : `${compareDateStr} ${ts}`,
                rangeCompareEndTime: isCustomCmp
                    ? `${customDe} ${customTe}`
                    : `${compareDateStr} ${te}`,
                compareStartIndex: isCustomCmp
                    ? customDs === customDe
                        ? customTs
                        : '00:00:00'
                    : ts,
                compareEndIndex: isCustomCmp ? customTe : te,
            };
            params = {
                ...params,
                ...cmpParams,
            };
        } else {
            // 跨天
            const compareDate = parseDate(compareDateStr);
            const theDayBeforeCompareDate = addDate(compareDate, -1, 'day');
            params = {
                customRange: true,
                rangeStartTime: `${ds} ${ts}`,
                rangeEndTime: `${de} ${te}`,
                rangeCompareStartTime: isCustomCmp
                    ? `${customDs} ${customTs}`
                    : `${formatDay(theDayBeforeCompareDate)} ${ts}`,
                rangeCompareEndTime: isCustomCmp
                    ? `${customDe} ${customTe}`
                    : `${compareDateStr} ${te}`,
                startIndex: '00:00:00',
                endIndex: te,
                realTime: realtime.day,
                compareTime: compareDateStr,
                date: compareDateStr,
                customCompareType: customCompareType
            };
            params.crossDay = true;
            params.compareStartIndex = isCustomCmp
                ? customDs === customDe
                    ? customTs
                    : '00:00:00'
                : '00:00:00';
            params.compareEndIndex = isCustomCmp ? customTe : te;
        }
    }

    return {
        ...params,
        quickCompareType,
    };
};

// 时间选择器切换label
function SwitchLabel({children, tips}) {
    return (
        <>
            {children}
            <Annotation mouseThrough placement='right'>
                {tips}
            </Annotation>
        </>
    );
}

SwitchLabel.propTypes = {
    children: PropTypes.node.isRequired,
    tips: PropTypes.node.isRequired,
};

function isTimeRangeDisabled(start, end) {
    return !end || !start ? false : timeToSeconds(start) >= timeToSeconds(end, true);
}

export async function loadGeRealtimeCompareTimeGroupConfig(params, menuCode) {
    return formatTimeGroupConfig({
        ...params,
        menuCode,
    })
    // try {
    //     const {data} = await ajax({
    //         url: getDucc('web', 'realtime'),
    //         withCredentials: true,
    //     });
    //     const a = eval(data)({
    //         ...params,
    //         menuCode,
    //     })
    //     return eval(data)({
    //         ...params,
    //         menuCode,
    //     });
    // } catch {
    //     return {};
    // }
}

export function GeRealtimeCompareTimeGroupConfigLoader(menuCode) {
    return params => loadGeRealtimeCompareTimeGroupConfig(params, menuCode);
}

export function GeRealtimeCompareTimeGroupInfoLoader(menuCode) {
    return {};
    // return async () => {
    //     try {
    //         const {body} = await ajax({
    //             withCredentials: true,
    //             url: addParams(
    //                 `http://${
    //                     isOnline ? 'ge' : 'yufa-ge'
    //                 }.back.jd.com/hjycenter/api/common/getRefreshInterval`,
    //                 {
    //                     appCode: 'hjy-pc',
    //                     language: 'zh',
    //                     ...(!isOnline ? {'host-ip': '11.66.181.212'} : null),
    //                 },
    //             ),
    //         });
    //         return {
    //             ...body,
    //             refreshInterval: body[menuCode] ?? defaultRefreshInterval,
    //         };
    //     } catch {
    //         return {};
    //     }
    // };
}

export function GeRealtimeCompareTimeGroupKernel({
                                                     getNow: defaultGetNow = () => new Date(),
                                                     getInfo = () => ({
                                                         refreshInterval: defaultRefreshInterval,
                                                     }),
                                                     getConfig = () => ({}),
                                                     getServerNow: defaultGetServerNow = () => new Date(),
                                                     // getServerNow: defaultGetServerNow = async () => {
                                                     //     // 获取服务器时间
                                                     //     const {
                                                     //         body: {time},
                                                     //     } = await ajax({
                                                     //         url: 'http://ge.back.jd.com/hjy/ge/common/getRealTime',
                                                     //         withCredentials: true,
                                                     //     });
                                                     //     const [Y, M, D, h, m, s] = time.split(/[ :-]/);
                                                     //
                                                     //     return new Date(Y, M - 1, D, h, m, s);
                                                     // },
                                                 } = {}) {
    return {
        component: ComboTimePicker,
        valueDestructuring,
        async reducer({
                          actionType,
                          actionData,
                          setState,
                          dispatch,
                          getProps,
                          fetch,
                          isInit,
                          changedByOther,
                          $$submit,
                      }) {
            const props = getProps() ?? {};
            const {
                value: {realtime, compareTime} = {},
                countdown,
                refreshDayRandom = defaultRefreshDayRandom,
            } = props;
            let getNow = props.getNow;

            let newRealtime, newCompareTime, newCompareTimeKey;
            let quickCompareOption;
            let specialCustomCompare; // 特殊的自定义对比
            let toSet;

            const init = isInit();
            const byUserAction = actionType === $$userAction;

            // 加载配置
            if (init || byUserAction) {
                const {
                    debugMode = false,
                    disableAll = false,
                    refreshInterval = defaultRefreshInterval,
                    operations: nextOperations = [],
                    refreshDayRandom: nextRefreshDayRandom,
                    disableAutoRefresh,
                } = await fetch(async () => {
                    try {
                        const info = await getInfo();
                        const config = await getConfig({
                            info,
                            getNow: () => getNow(),
                            formatDay,
                            formatDate,
                            addDate,
                            inTimeRange: (x, dt = getNow()) => inTimeRange(x, dt),
                            getLunarYoy: findYOY,
                            parseTime: x => parseTime(...x.split(' ')),
                            parseDate,
                            isOnline,
                            DayPanel,
                            RangePanel,
                            RangeTimePanel,
                        });

                        return {
                            ...info,
                            ...config,
                        };
                    } catch {
                        return {};
                    }
                });

                toSet = {
                    ...toSet,
                    disableAll,
                    operations: nextOperations,
                    refreshDayRandom: nextRefreshDayRandom,
                    refreshingDay: false,
                    disableAutoRefresh,
                };

                // TAG 初始化
                if (init) {
                    toSet = {
                        ...toSet,
                        dispatch,
                    };

                    if (debugMode) {
                        const getDebugNow = () => {
                            const {debugStartAt, debugTime} = getProps();

                            const result = debugStartAt
                                ? new Date(debugTime.getTime() + (new Date() - debugStartAt))
                                : new Date();

                            return result;
                        };

                        // TAG 调试模式，覆盖getNow和getServerNow
                        toSet = {
                            ...toSet,
                            debugMode: true,
                            debugStartAt: new Date(), // 调试起始时间
                            debugTime: new Date(), // 调试当前时间
                            getNow: getDebugNow,
                            getServerNow: getDebugNow,
                        };
                    } else {
                        toSet = {
                            ...toSet,
                            getNow: defaultGetNow,
                            getServerNow: defaultGetServerNow,
                        };
                    }
                }

                // 如果是用户动作，触发倒计时
                // 调试模式下，缩短倒计时
                if (byUserAction) {
                    dispatch($$countdown, debugMode ? 1000 : refreshInterval);
                }
            }

            getNow = toSet?.getNow ?? getNow;
            if (!getNow) {
                return;
            }

            // 禁用当前时间之后的时间
            const isTimeDisabled = v => {
                const now = getNow();
                const [h, m, s = 0] = v.split(':').map(x => parseInt(x, 10));
                return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, s) > now;
            };
            const now = getNow();
            const todayStr = formatDay(now);
            const yesterdayStr = formatDay(addDate(now, -1, 'day'));

            // 默认实时
            const defaultRealtime = {
                key: 'today',
                day: todayStr,
                value: ['00:00:00', formatDate(now, 'hh:mm:ss')],
                type: 'rangeTime',
            };

            if (init) {
                newRealtime = defaultRealtime;
            }

            // 跨零点刷新生效
            if (actionType === $$refreshDayUpdate) {
                if (!props.prompt) {
                    newRealtime = defaultRealtime;
                }
            }

            // 倒计时
            if (actionType === $$countdown) {
                if (actionData <= 0) {
                    setState({
                        countdown: null,
                        disabledRefresh: false,
                    });
                } else {
                    setState({
                        countdown: Math.floor(actionData / 1000),
                    });

                    setTimeout(() => {
                        dispatch($$countdown, actionData - 1000);
                    }, 1000);
                }
                return;
            }

            // 更新时间
            if (actionType === $$updateRealtime) {
                let day;
                switch (actionData.key) {
                    case 'today':
                        day = todayStr;
                        break;
                    case 'yesterday':
                        day = yesterdayStr;
                        break;
                    default:
                        day = actionData.value[1][0];
                        break;
                }
                newRealtime = {
                    ...actionData,
                    day,
                };
            }

            // 刷新
            if (actionType === $$refresh) {
                switch (realtime.key) {
                    case 'today':
                        newRealtime = {
                            ...realtime,
                            day: todayStr,
                            value: [realtime.value[0], formatDate(now, 'hh:mm:ss')],
                        };
                        break;
                    case 'custom':
                        if (realtime.value[1][0] === todayStr) {
                            newRealtime = {
                                ...realtime,
                                day: todayStr,
                                value: [
                                    realtime.value[0],
                                    [realtime.value[1][0], formatDate(now, 'hh:mm:ss')],
                                ],
                            };
                        }
                        break;
                    default:
                        break;
                }
            }

            // 更新调试时间
            if (actionType === $$updateDebugTime) {
                toSet = {
                    ...toSet,
                    debugTime: actionData,
                    debugStartAt: new Date(),
                };
            }

            // TAG 快捷选项
            if (actionType === $$operation) {
                const v = actionData.value();
                newRealtime = v.realtime;
                if (v.compareTime) {
                    quickCompareOption = {
                        ...v.compareTime,
                        label: actionData.label,
                        key: actionData.label,
                    };
                    newCompareTime = {
                        type: 'quick',
                        value: getValueFromQuickOption(quickCompareOption),
                    };
                } else {
                    newCompareTimeKey = v.compareTimeKey;
                }
                newRealtime.quickCompareType = actionData.key;
                newRealtime.item = actionData;
                if (actionData.specialCustomCompare) {
                    specialCustomCompare = actionData.specialCustomCompare();
                }
            }

            if (actionType === $$refreshDay) {
                if (realtime?.key === 'today') {
                    const curKey = panelKey;
                    setTimeout(() => {
                        if (curKey === getProps().valueKey && isDocumentVisible()) {
                            dispatch($$refreshDayUpdate, curKey);
                        }
                    }, refreshDayRandom());
                }
            }

            // 更新对比日期
            if (actionType === $$updateCompareTime) {
                newCompareTime = actionData;
            }

            const timeChanged = newRealtime || newCompareTime;
            const nextRealtime = newRealtime || realtime;

            // 触发用户动作
            if ((timeChanged || changedByOther) && !init) {
                setState({
                    disabledRefresh: true,
                });

                // 启动倒计时
                if (countdown == null) {
                    dispatch($$userAction);
                }
            }

            // TAG 更新实时日期后，更新面板
            if (newRealtime) {
                // 基础日期
                const base = parseDate(nextRealtime.day);

                // 判断是否跨天
                let crossDay = false; // 跨天
                let startDate, endDate;

                if (nextRealtime.type === 'rangeDayTime') {
                    const [[sd, st], [ed, et]] = nextRealtime.value;
                    startDate = parseTime(sd, st);
                    endDate = parseTime(ed, et);
                    crossDay = sd !== ed;
                } else {
                    const {
                        day,
                        value: [st, et],
                    } = nextRealtime;
                    startDate = parseTime(day, st);
                    endDate = parseTime(day, et);
                }

                // 时间面板禁用关闭
                const isTimePickerDisableClose = ([startValue, endValue]) =>
                    !startValue ||
                    !endValue ||
                    (!endValue.split(':')[1].endsWith(9) && endValue.split(':')[1] !== '01');

                // 日期面板禁用关闭
                const isDatePickerDisableClose = ([d = [], t = []] = []) =>
                    !d[0] || !d[1] || isTimePickerDisableClose(t);

                const startTimePickerConfig = {
                    showHour: true,
                    showMinute: true,
                    minuteStep: 10,
                    customReplace: (v, idx, newValue) => {
                        v = v ?? '00:00:00';
                        const valueArr = v.split(':');
                        valueArr[idx] = newValue;
                        if (idx === 1) {
                            valueArr[2] = '00';
                        }
                        return valueArr.join(':');
                    },
                };

                const endTimePickerConfig = {
                    sequence: [
                        {
                            list: Array(24)
                                .fill(null)
                                .map((_v, i) => fixZero(i)),
                        },
                        {
                            list: ['01', '09', 19, 29, 39, 49, 59],
                        },
                    ],
                    customReplace: (v, idx, newValue) => {
                        v ??= '00:00:00';
                        const valueArr = v.split(':');
                        valueArr[idx] = newValue;
                        if (idx === 1) {
                            valueArr[2] = 59;
                        }
                        return valueArr.join(':');
                    },
                };

                // 范围面板配置
                const RangePanelConfig = {
                    ...SpecialRangeDayTimePanel,
                    isItemDisabled(ts, te, [[ds], [de]]) {
                        // 如果是同一天，起始不能超过结束
                        if (!ds || !de) {
                            return false;
                        }
                        if (ds === de) {
                            return isTimeRangeDisabled(ts, te);
                        }
                        return false;
                    },
                    startTimeConfig: {
                        ...startTimePickerConfig,
                        isValueDisabled(v, [[ds]]) {
                            if (!ds) {
                                return false;
                            }
                            if (ds === todayStr) {
                                return isTimeDisabled(v);
                            }
                            return false;
                        },
                    },
                    endTimeConfig: {
                        ...endTimePickerConfig,
                        isValueDisabled(v, [, [de]]) {
                            if (!de) {
                                return false;
                            }
                            if (de === todayStr) {
                                return isTimeDisabled(v);
                            }
                            return false;
                        },
                    },
                    isDisableClose: isDatePickerDisableClose,
                };

                // 单日面板回显
                const dayPanelEcho =
                    defaultDay =>
                        ([st, et] = [], {day = defaultDay} = {}) =>
                            (
                                <>
                                    {day} {st ?? <Placeholder>起始时间</Placeholder>}
                                    <br/>
                                    {day} {et ?? <Placeholder>结束时间</Placeholder>}
                                </>
                            );

                // 实时面板
                const realtimePanels = [
                    {
                        ...RangeTimePanel,
                        label: '今日',
                        key: 'today',
                        echo: dayPanelEcho(todayStr),
                        // 范围的start必须小于end
                        isItemDisabled: isTimeRangeDisabled,
                        startConfig: {
                            ...startTimePickerConfig,
                            isValueDisabled: isTimeDisabled,
                        },

                        endConfig: {
                            ...endTimePickerConfig,
                            isValueDisabled: isTimeDisabled,
                        },
                        isDisableClose: isTimePickerDisableClose,
                    },
                    {
                        ...RangeTimePanel,
                        label: (
                            <SwitchLabel tips='此部分数据为昨日实时累计数据，用于辅助用户6点前查看昨日数据。'>
                                昨日
                            </SwitchLabel>
                        ),
                        key: 'yesterday',
                        // 范围的start必须小于end
                        isItemDisabled: isTimeRangeDisabled,
                        echo: dayPanelEcho(yesterdayStr),
                        startConfig: startTimePickerConfig,
                        endConfig: endTimePickerConfig,
                        isDisableClose: isTimePickerDisableClose,
                    },
                    {
                        ...RangePanelConfig,
                        dateConfig: {
                            isDateDisabled: date => {
                                const diff = diffInDay(getNow(), date);
                                return diff < 0 || diff > 2;
                            },
                            maxRange: 2,
                        },
                        label: (
                            <SwitchLabel tips='支持跨天选时间段，最长可选48小时，最早可以选到前日零点，最晚可以选到今日现在。'>
                                自定义
                            </SwitchLabel>
                        ),
                        key: 'custom',
                    },
                ];

                // 对边时间——快捷选择选项
                const quickOptions = [
                    {
                        key: 'yoy',
                        label: '同比',
                        circle: d => addDate(d, -1, 'year'),
                    },
                    {
                        key: 'dod',
                        label: '环比',
                        circle: d => addDate(d, -1, 'day'),
                    },
                    // !crossDay && {
                    //     key: 'dod',
                    //     label: '环比昨日',
                    //     circle: d => addDate(d, -1, 'day'),
                    // },
                    {
                        key: 'lunarYoy',
                        label: '农历同比',
                        circle: d => findYOY(d),
                        lunar: true,
                    },
                ]
                    .filter(x => x)
                    .map(({key, label, circle, lunar}) => {
                        const circleRange = [startDate, endDate].map(circle);

                        return {
                            key,
                            label,
                            type: 'rangeDayTime',
                            lunar,
                            value: circleRange.map(t => [formatDay(t), formatDate(t, 'hh:mm:ss')]),
                        };
                    })
                    .concat(quickCompareOption ?? []);

                // 对比面板回显
                const compareEcho = (v, arrow) => {
                    if (v.type === 'quick') {
                        v = v.value;
                    }
                    const {lunar} = v;
                    let start, end;

                    function toDateString(dateArray) {
                        const date = parseTime(...dateArray);
                        if (lunar) {
                            return `${formatDay(date)} ${toString(Lunar(date))} ${formatDate(
                                date,
                                'hh:mm:ss',
                            )}`;
                        }
                        return formatDate(date, 'YYYY-MM-DD hh:mm:ss');
                    }

                    if (v.type === 'rangeDayTime') {
                        [start, end] = v.value;
                    } else {
                        const {
                            customRange,
                            rangeCompareStartTime,
                            rangeCompareEndTime,
                            compareTime: compareTimeV,
                            startIndex,
                            endIndex,
                        } = valueDestructuring({
                            realtime: nextRealtime,
                            compareTime: v,
                        });

                        if (customRange) {
                            start = rangeCompareStartTime.split(' ');
                            end = rangeCompareEndTime.split(' ');
                        } else {
                            start = [compareTimeV, startIndex];
                            end = [compareTimeV, endIndex];
                        }
                    }

                    const main = (
                        <>
                            {toDateString(start)}
                            <br/>
                            {toDateString(end)}
                        </>
                    );

                    if (arrow) {
                        return (
                            <div className='jmtp-ge-realtime-compare-time-group-compare-echo'>
                                {main}
                                <Icon className='jmti-backward jmtp-ge-realtime-compare-time-group-compare-echo-arrow'/>
                            </div>
                        );
                    }

                    return main;
                };

                const customDateConfig = {
                    isDateDisabled: date =>
                        !inRange(date, parseDate('2021-01-01'), addDate(base, -3, 'day')),
                    maxRange: 2,
                };

                // 对比面板
                const enableNormalPanel = !crossDay && !specialCustomCompare;
                const compareTimePanels = [
                    {
                        ...QuickPanel,
                        key: 'quickCompare',
                        label: '快捷对比',
                        options: quickOptions.map(v => ({
                            ...v,
                            echo: compareEcho(
                                {
                                    type: 'quick',
                                    value: getValueFromQuickOption(v),
                                },
                                true,
                            ),
                        })),
                    },
                    enableNormalPanel && {
                        ...DayPanel,
                        key: 'recent30day',
                        label: '按天',
                        isDateDisabled: date =>
                            !inRange(
                                date,
                                parseDate('2020-01-01'),
                                addDate(base, crossDay ? -2 : -1, 'day'),
                            ),
                    },
                    // ...findPromoteMonth().map(([year, month]) => {
                    //     const monthDay = new Date(year, month - 1, 1);
                    //     const label = formatDate(monthDay, 'YYYY-MM');
                    //
                    //     return (
                    //         enableNormalPanel && {
                    //             ...DayPanel,
                    //             key: label,
                    //             label,
                    //             defaultPickerValue: [year, month],
                    //             isDateDisabled: date =>
                    //                 !inRange(
                    //                     date,
                    //                     getStartOfMonth(monthDay),
                    //                     getEndOfMonth(monthDay),
                    //                 ),
                    //         }
                    //     );
                    // }),
                    // enableNormalPanel && {
                    //     ...DayPanel,
                    //     key: 'month-on-year',
                    //     label: (
                    //         <SwitchLabel tips='支持与去年同期月份进行对比。'>月同比</SwitchLabel>
                    //     ),
                    //     isDateDisabled: date =>
                    //         !inRange(
                    //             date,
                    //             getStartOfMonth(addDate(base, -1, 'year')),
                    //             getEndOfMonth(addDate(base, -1, 'year')),
                    //         ),
                    //     defaultPickerValue: [now.getFullYear() - 1, now.getMonth() + 1],
                    // },
                    // specialCustomCompare
                    //     ? {
                    //           ...specialCustomCompare.props,
                    //           key: 'customCompare',
                    //           label: (
                    //               <SwitchLabel tips={specialCustomCompare.tips}>自定义</SwitchLabel>
                    //           ),
                    //       }
                    //     : {
                    //           ...RangePanelConfig,
                    //           dateConfig: customDateConfig,
                    //           key: 'customCompare',
                    //           label: (
                    //               <SwitchLabel tips='支持选择历史48H内分时段数据进行对比'>
                    //                   自定义
                    //               </SwitchLabel>
                    //           ),
                    //       },
                ].filter(x => x);

                if (!newCompareTime) {
                    let newQuickOption;
                    if (newCompareTimeKey) {
                        newQuickOption = quickOptions.find(o => o.key === newCompareTimeKey);
                    }
                    newQuickOption ??= quickOptions[0];

                    // 更新对比时间
                    newCompareTime = {
                        type: 'quick',
                        value: getValueFromQuickOption(newQuickOption),
                    };
                }

                toSet = {
                    ...toSet,
                    valueKey: ++panelKey,
                    panels: realtimePanels,
                    comparePanels: compareTimePanels.map(p => {
                        if (p.type === 'rangeDayTime') {
                            return p;
                        }
                        return {
                            ...p,
                            echo: (_, v) => compareEcho(v),
                        };
                    }),
                };
            }

            // 更新值
            if (newRealtime || newCompareTime) {
                toSet = {
                    ...toSet,
                    value: {
                        // 实时时间默认值
                        realtime: newRealtime ?? realtime,

                        // 对比时间默认值
                        compareTime: newCompareTime ?? compareTime,
                    },
                };
            }

            if (toSet) {
                setState({
                    ...toSet,
                });

                if ($$submit) {
                    dispatch($$submit);
                }
            } else if ($$submit && changedByOther) {
                dispatch($$submit);
            }
        },
    };
}

export default function GeRealtimeCompareTimeGroupNew({menuCode, ...arg}) {
    if (menuCode) {
        arg = {
            ...arg,
            getConfig: GeRealtimeCompareTimeGroupConfigLoader(menuCode), // 参数是menuId
            getInfo: GeRealtimeCompareTimeGroupInfoLoader(menuCode),
        };
    }
    const {component, valueDestructuring, reducer} = GeRealtimeCompareTimeGroupKernel(arg);

    return {
        noFormWrap: true,
        fields: [
            {
                name: 'realtime',
                component,
                valueDestructuring,
                async reducer({
                                  actionType,
                                  actionData,
                                  setState,
                                  dispatch,
                                  getProps,
                                  fetch,
                                  isGroupReady,
                                  changedState: {realtime: rt, __realtime: __rt, ...othersChangedState},
                              }) {
                    return reducer({
                        actionType,
                        actionData,
                        setState(props) {
                            setState({props});
                        },
                        dispatch,
                        getProps,
                        async fetch(...args) {
                            const [, result] = await fetch(...args);
                            return result;
                        },
                        isInit() {
                            return actionType === FormGroup.INIT;
                        },
                        changedByOther:
                            Object.keys(othersChangedState).length && isGroupReady('Header'),
                    });
                },
            },
        ],
    };
}
